import React, { useState, useEffect } from 'react';
import './DataEditor.css';

const DataEditor = ({ 
  data = [], 
  onDataChange, 
  onClose, 
  comparisonMode = 'class',
  dataSource = 'database' 
}) => {
  const [viewMode, setViewMode] = useState('friendly'); // 'friendly' or 'json'
  const [editableData, setEditableData] = useState([]);
  const [jsonString, setJsonString] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Initialize data
  useEffect(() => {
    if (data && data.length > 0) {
      setEditableData(data);
      setJsonString(JSON.stringify(data, null, 2));
    } else {
      // Generate sample data structure for new datasets
      const sampleData = generateSampleData();
      setEditableData(sampleData);
      setJsonString(JSON.stringify(sampleData, null, 2));
    }
  }, [data]);

  const generateSampleData = () => {
    return [
      {
        name: 'Sample Group 1',
        data: [85, 90, 78, 92, 88, 76, 94, 82, 89, 91],
        min: 76,
        q1: 82,
        median: 88.5,
        q3: 91,
        max: 94,
        mean: 86.5,
        iqr: 9,
        outliers: []
      },
      {
        name: 'Sample Group 2',
        data: [72, 85, 69, 88, 81, 73, 90, 77, 84, 86],
        min: 69,
        q1: 73,
        median: 82.5,
        q3: 86,
        max: 90,
        mean: 80.5,
        iqr: 13,
        outliers: []
      }
    ];
  };

  const handleFriendlyDataChange = (index, field, value) => {
    const newData = [...editableData];
    if (field === 'data') {
      // Handle data array input
      try {
        const dataArray = value.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
        newData[index].data = dataArray;
        // Recalculate statistics
        newData[index] = { ...newData[index], ...calculateStats(dataArray) };
      } catch (error) {
        console.error('Error parsing data array:', error);
      }
    } else if (field === 'name') {
      newData[index].name = value;
    } else {
      // Handle numeric fields
      newData[index][field] = parseFloat(value) || 0;
    }
    
    setEditableData(newData);
    setJsonString(JSON.stringify(newData, null, 2));
    onDataChange(newData);
  };

  const handleJsonChange = (value) => {
    setJsonString(value);
    try {
      const parsedData = JSON.parse(value);
      if (Array.isArray(parsedData)) {
        setEditableData(parsedData);
        onDataChange(parsedData);
      }
    } catch (error) {
      // Invalid JSON, don't update data
      console.log('Invalid JSON, waiting for valid input...');
    }
  };

  const calculateStats = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return {};
    
    const sorted = [...dataArray].sort((a, b) => a - b);
    const n = sorted.length;
    const min = sorted[0];
    const max = sorted[n - 1];
    const mean = dataArray.reduce((sum, val) => sum + val, 0) / n;
    
    const q1Index = Math.floor(n * 0.25);
    const medianIndex = Math.floor(n * 0.5);
    const q3Index = Math.floor(n * 0.75);
    
    const q1 = sorted[q1Index];
    const median = n % 2 === 0 ? (sorted[medianIndex - 1] + sorted[medianIndex]) / 2 : sorted[medianIndex];
    const q3 = sorted[q3Index];
    const iqr = q3 - q1;
    
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    const outliers = dataArray.filter(val => val < lowerBound || val > upperBound);
    
    return {
      min: Math.round(min * 100) / 100,
      q1: Math.round(q1 * 100) / 100,
      median: Math.round(median * 100) / 100,
      q3: Math.round(q3 * 100) / 100,
      max: Math.round(max * 100) / 100,
      mean: Math.round(mean * 100) / 100,
      iqr: Math.round(iqr * 100) / 100,
      outliers
    };
  };

  const addNewGroup = () => {
    const newGroup = {
      name: `New Group ${editableData.length + 1}`,
      data: [70, 75, 80, 85, 90],
      ...calculateStats([70, 75, 80, 85, 90])
    };
    
    const newData = [...editableData, newGroup];
    setEditableData(newData);
    setJsonString(JSON.stringify(newData, null, 2));
    onDataChange(newData);
  };

  const deleteGroup = (index) => {
    if (window.confirm('確定要刪除這個群組嗎？\nAre you sure you want to delete this group?')) {
      const newData = editableData.filter((_, i) => i !== index);
      setEditableData(newData);
      setJsonString(JSON.stringify(newData, null, 2));
      onDataChange(newData);
    }
  };

  const filteredAndSortedData = editableData
    .filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      const modifier = sortOrder === 'asc' ? 1 : -1;
      
      if (typeof aVal === 'string') {
        return aVal.localeCompare(bVal) * modifier;
      }
      return (aVal - bVal) * modifier;
    });

  return (
    <div className="data-editor-modal">
      <div className="data-editor-container">
        <div className="data-editor-header">
          <div className="header-left">
            <h3>📊 數據編輯器 Data Editor</h3>
            <span className="data-source-badge">
              {dataSource === 'imported' ? '📂 匯入數據' : '💾 資料庫數據'} 
              ({editableData.length} 群組)
            </span>
          </div>
          <div className="header-right">
            <div className="view-toggle">
              <button 
                className={`toggle-btn ${viewMode === 'friendly' ? 'active' : ''}`}
                onClick={() => setViewMode('friendly')}
              >
                👥 友善檢視 User Friendly
              </button>
              <button 
                className={`toggle-btn ${viewMode === 'json' ? 'active' : ''}`}
                onClick={() => setViewMode('json')}
              >
                🔧 JSON 檢視 JSON View
              </button>
            </div>
            <button className="close-button" onClick={onClose}>
              ✕ 關閉 Close
            </button>
          </div>
        </div>

        <div className="data-editor-content">
          {viewMode === 'friendly' ? (
            <div className="friendly-view">
              <div className="controls-bar">
                <div className="search-controls">
                  <input
                    type="text"
                    placeholder="🔍 搜尋群組 Search groups..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="name">按名稱排序 Sort by Name</option>
                    <option value="mean">按平均值排序 Sort by Mean</option>
                    <option value="median">按中位數排序 Sort by Median</option>
                    <option value="min">按最小值排序 Sort by Min</option>
                    <option value="max">按最大值排序 Sort by Max</option>
                  </select>
                  <button 
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="sort-order-btn"
                  >
                    {sortOrder === 'asc' ? '⬆️ 升序' : '⬇️ 降序'}
                  </button>
                </div>
                <button onClick={addNewGroup} className="add-group-btn">
                  ➕ 新增群組 Add Group
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>群組名稱 Group Name</th>
                      <th>數據點 Data Points</th>
                      <th>最小值 Min</th>
                      <th>Q1</th>
                      <th>中位數 Median</th>
                      <th>Q3</th>
                      <th>最大值 Max</th>
                      <th>平均值 Mean</th>
                      <th>四分位距 IQR</th>
                      <th>異常值 Outliers</th>
                      <th>操作 Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedData.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => handleFriendlyDataChange(
                              editableData.indexOf(item), 'name', e.target.value
                            )}
                            className="name-input"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={item.data ? item.data.join(', ') : ''}
                            onChange={(e) => handleFriendlyDataChange(
                              editableData.indexOf(item), 'data', e.target.value
                            )}
                            placeholder="70, 75, 80, 85, 90"
                            className="data-input"
                            title="Enter comma-separated numbers"
                          />
                        </td>
                        <td className="stat-cell">{item.min}</td>
                        <td className="stat-cell">{item.q1}</td>
                        <td className="stat-cell">{item.median}</td>
                        <td className="stat-cell">{item.q3}</td>
                        <td className="stat-cell">{item.max}</td>
                        <td className="stat-cell">{item.mean}</td>
                        <td className="stat-cell">{item.iqr}</td>
                        <td className="outliers-cell">
                          {item.outliers && item.outliers.length > 0 
                            ? item.outliers.join(', ') 
                            : '無 None'
                          }
                        </td>
                        <td>
                          <button 
                            onClick={() => deleteGroup(editableData.indexOf(item))}
                            className="delete-btn"
                            title="Delete this group"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="json-view">
              <div className="json-controls">
                <h4>JSON 編輯模式 JSON Edit Mode</h4>
                <p>直接編輯JSON數據。修改後會自動更新統計數據。</p>
                <p>Edit JSON data directly. Statistics will be updated automatically.</p>
              </div>
              <textarea
                value={jsonString}
                onChange={(e) => handleJsonChange(e.target.value)}
                className="json-textarea"
                placeholder="Enter JSON data here..."
                spellCheck={false}
              />
              <div className="json-info">
                <small>
                  💡 提示 Tip: 確保JSON格式正確，每個群組包含 name, data, 和統計數據字段
                  <br />
                  Ensure JSON format is correct with name, data, and statistics fields for each group
                </small>
              </div>
            </div>
          )}
        </div>

        <div className="data-editor-footer">
          <div className="footer-info">
            <span>總群組數 Total Groups: {editableData.length}</span>
            <span>總數據點 Total Data Points: {editableData.reduce((sum, item) => sum + (item.data?.length || 0), 0)}</span>
          </div>
          <div className="footer-actions">
            <button onClick={onClose} className="save-close-btn">
              💾 儲存並關閉 Save & Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataEditor;
